/**
 * Whether the staff affected by an appointment change would actually see the notification it
 * raises. Two things make it a no-op regardless of the "notify staff" checkbox:
 * - The only affected staff member is the person making the change; the backend never notifies
 *   someone about their own action.
 * - The clinic notification privilege table has the type off for the affected role(s). Judged
 *   against the clinic default, since the scheduler cannot see anyone's personal override.
 *
 * @param {object} params
 * @param {string[]} params.userIds Assigned staff user ids potentially affected by the change.
 * @param {{ id: string, role: string }[]} params.users Full user list, to resolve a role per id.
 * @param {Record<string, Record<string, { inApp?: boolean }>>} params.clinicNotificationSettings
 * Keyed by role then notification type, as stored on the auth store's `clinicNotificationSettings`.
 * @param {string} [params.currentUserId] The user performing the change.
 * @param {string} params.notificationType The notification type being judged, e.g. `cancelledAppointment`.
 * @return {'none' | 'enabled' | 'partial' | 'disabled' | 'disabledSelf'} `none` - there is no
 * assigned staff member to notify at all. `enabled` - every affected recipient would be notified.
 * `partial` - some would, some wouldn't. `disabled` - none would, due to notification settings.
 * `disabledSelf` - the only affected staff member is `currentUserId`.
 */
export function getNotificationAvailability({
  userIds, users, clinicNotificationSettings, currentUserId = null, notificationType,
}) {
  if (!userIds.length) {
    return 'none';
  }

  const recipientUserIds = currentUserId
    ? userIds.filter((userId) => userId !== currentUserId)
    : userIds;

  if (!recipientUserIds.length) {
    return 'disabledSelf';
  }

  const roles = [...new Set(
    recipientUserIds
      .map((userId) => users.find((user) => user.id === userId)?.role)
      .filter(Boolean),
  )];

  if (!roles.length) {
    return 'enabled';
  }

  const isRoleEnabled = (role) => clinicNotificationSettings?.[role]?.[notificationType]?.inApp ?? false;

  const enabledCount = roles.filter(isRoleEnabled).length;

  if (enabledCount === 0) {
    return 'disabled';
  }

  if (enabledCount < roles.length) {
    return 'partial';
  }

  return 'enabled';
}

/**
 * Every value a `notifyStaffAvailability` prop may hold. A superset of what the function above
 * returns: the `NoChange` pair is the caller's verdict on a change that would tell the assignee
 * nothing - an appointment never saved, or one already in the status being applied - which this
 * module cannot see. `partialNoChange` still sends, just not for every affected appointment.
 */
export const NOTIFY_STAFF_AVAILABILITY = [
  'none', 'enabled', 'partial', 'partialNoChange',
  'disabled', 'disabledSelf', 'disabledUnsaved', 'disabledNoChange',
];

/** Values where checking the box would do nothing, so it is shown greyed instead. */
const DISABLED_AVAILABILITY = ['disabled', 'disabledSelf', 'disabledUnsaved', 'disabledNoChange'];

/**
 * Hidden only when there is nobody to notify, or the caller cannot notify staff at all.
 * @param {string} availability
 * @return {boolean}
 */
export function isNotifyStaffShown(availability) {
  return availability !== 'none';
}

/**
 * Disabled when none of the affected staff would actually see the notification.
 * @param {string} availability
 * @return {boolean}
 */
export function isNotifyStaffDisabled(availability) {
  return DISABLED_AVAILABILITY.includes(availability);
}

/**
 * Whether the box should start checked. These three live together so a dialog seeds its state and
 * guards its `ok` payload from the same rule NotifyStaffCheckbox renders from.
 * @param {string} availability
 * @return {boolean}
 */
export function isNotifyStaffDefaultChecked(availability) {
  return isNotifyStaffShown(availability) && !isNotifyStaffDisabled(availability);
}
